import React, { useState, useRef, useEffect } from 'react';
import { findResponse } from './chatbotUtils';
import { API_BASE } from '../config/runtime';
import {
  getCurrentUser,
  isUserLoggedIn,
  getUserId,
  clearChatbotSessionData,
  clearGuestSessionData,
} from './chatbotAuthUtils';
import {
  broadcastEscalation,
  storeConversationContext,
  addUnreadEscalation,
  subscribeToPriorityUpdates,
  initializeEscalationSystem,
  cleanupEscalation,
} from '../services/escalationService';
import { getQueueForTicket } from '../services/messageQueueService';
import { realtimeEvents, EVENT_TYPES } from '../services/realtimeEventSystem';
import { mockWebSocket, initializeMockWebSocket } from '../services/mockWebSocketBridge';

const WELCOME_MESSAGE = {
  id: 1,
  text: 'Welcome to Hygea Support! 👋 I\'m here to help you with booking appointments, service information, branch locations, and any questions about our wellness services. How can I assist you today?',
  sender: 'bot',
  timestamp: new Date(),
  isTyping: false,
};

const ChatBot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [isBlockedByAdmin, setIsBlockedByAdmin] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [realtimeActive, setRealtimeActive] = useState(false);
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const authCheckIntervalRef = useRef(null);
  const lastAuthStateRef = useRef(null);
  const escalationSystemRef = useRef(null);
  const unsubscribeRealtimeRef = useRef(() => {});

  // Auto-scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize on component mount - check authentication state and load history
  useEffect(() => {
    const initializeChat = async () => {
      try {
        // Initialize mock WebSocket for real-time communication
        await initializeMockWebSocket();
        setConnectionStatus('connected');
        setRealtimeActive(true);

        // Listen for connection status changes
        unsubscribeRealtimeRef.current = realtimeEvents.on(
          EVENT_TYPES.CONNECTION_STATUS,
          (data) => {
            setConnectionStatus(data.status);
          }
        );
      } catch (error) {
        console.error('[ChatBot] Real-time system initialization failed:', error);
        setConnectionStatus('disconnected');
      }

      const user = getCurrentUser();
      const isLoggedIn = isUserLoggedIn();
      const userId = getUserId();

      console.log(
        `[ChatBot Init] isLoggedIn: ${isLoggedIn}, userId: ${userId}`
      );

      if (isLoggedIn && userId) {
        setCurrentUserId(userId);
        // Load conversation history for authenticated user
        await loadConversationHistory(userId);
      } else {
        // Not logged in - ensure clean guest state
        setCurrentUserId(null);
        clearGuestSessionData();
        clearChatbotSessionData();
        // Reset to welcome message
        setMessages([WELCOME_MESSAGE]);
      }

      lastAuthStateRef.current = isLoggedIn ? userId : null;
    };

    initializeChat();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (unsubscribeRealtimeRef.current) {
        unsubscribeRealtimeRef.current();
      }
    };
  }, []);

  // Monitor authentication state changes
  useEffect(() => {
    const checkAuthState = () => {
      const isLoggedIn = isUserLoggedIn();
      const userId = getUserId();
      const currentState = isLoggedIn ? userId : null;

      // Check if auth state changed from last check
      if (JSON.stringify(currentState) !== JSON.stringify(lastAuthStateRef.current)) {
        console.log(
          `[ChatBot Auth Change] from ${lastAuthStateRef.current} to ${currentState}`
        );

        if (lastAuthStateRef.current && !currentState) {
          // User logged out
          handleLogout();
        } else if (!lastAuthStateRef.current && currentState) {
          // User logged in
          setCurrentUserId(currentState);
          loadConversationHistory(currentState);
        } else if (currentState && currentState !== lastAuthStateRef.current) {
          // Different user logged in
          setCurrentUserId(currentState);
          loadConversationHistory(currentState);
        }

        lastAuthStateRef.current = currentState;
      }
    };

    // Check auth state every 500ms
    authCheckIntervalRef.current = setInterval(checkAuthState, 500);

    return () => {
      if (authCheckIntervalRef.current) {
        clearInterval(authCheckIntervalRef.current);
      }
    };
  }, []);

  // Load conversation history for authenticated user
  const loadConversationHistory = async (userId) => {
    if (!userId) return;

    setIsLoadingHistory(true);
    try {
      const response = await fetch(
        `${API_BASE}/chatbot/get-conversations.php?user_id=${userId}`
      );
      const data = await response.json();

      if (data.success && Array.isArray(data.conversations) && data.conversations.length > 0) {
        console.log(`[ChatBot] Loaded ${data.conversations.length} messages for user ${userId}`);

        // Convert backend format to component format
        const loadedMessages = data.conversations.map((conv) => ({
          id: conv.id,
          text: conv.text,
          sender: conv.sender,
          timestamp: new Date(conv.timestamp),
          isBackendMessage: true,
          isHistoryLoaded: true,
        }));

        // If we have history and welcome message is present, remove welcome
        setMessages((prev) => {
          const hasWelcomeOnly =
            prev.length === 1 && prev[0].id === WELCOME_MESSAGE.id;
          if (hasWelcomeOnly) {
            return loadedMessages;
          }
          // Add new messages if not a full reload
          return prev.some((m) => m.isHistoryLoaded)
            ? prev
            : [...prev.filter((m) => !m.isHistoryLoaded), ...loadedMessages];
        });
      } else {
        console.log(
          `[ChatBot] No conversation history for user ${userId}, using welcome message`
        );
        // Keep welcome message if no history
        setMessages([WELCOME_MESSAGE]);
      }
    } catch (error) {
      console.error('[ChatBot] Error loading conversation history:', error);
      // On error, keep current or welcome message
      setMessages((prev) => (prev.length > 0 ? prev : [WELCOME_MESSAGE]));
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Reset chat to welcome message and clear session data
  const handleLogout = () => {
    console.log('[ChatBot] Handling logout - resetting state');

    // Stop polling if active
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Cleanup escalation system resources
    if (ticketId && escalationSystemRef.current) {
      cleanupEscalation(ticketId);
      escalationSystemRef.current = null;
    }

    // Reset states
    setMessages([WELCOME_MESSAGE]);
    setInput('');
    setIsThinking(false);
    setEscalated(false);
    setTicketId(null);
    setIsBlockedByAdmin(false);
    setCurrentUserId(null);

    // Clear all session data
    clearChatbotSessionData();
    clearGuestSessionData();

    console.log('[ChatBot] Logout complete - reset to initial state');
  };

  // Save message to backend for authenticated user
  const saveMessageToBackend = async (userId, messageText, sender) => {
    if (!userId) return;

    try {
      const response = await fetch(
        `${API_BASE}/chatbot/save-message.php`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            message: messageText,
            sender: sender,
            message_type: 'text',
          }),
        }
      );

      if (!response.ok) {
        console.error('[ChatBot] Failed to save message:', response.statusText);
        return;
      }

      const data = await response.json();
      if (!data.success) {
        console.error('[ChatBot] Backend error saving message:', data.message);
      }
    } catch (error) {
      console.error('[ChatBot] Error saving message to backend:', error);
    }
  };

  // Fetch messages for escalated conversation (live support)
  const fetchLiveSupportMessages = async (tickId) => {
    if (!tickId) {
      console.log('[ChatBot] fetchLiveSupportMessages called with no ticket ID');
      return;
    }

    console.log('[ChatBot] Fetching live support messages for ticket:', tickId);

    try {
      const response = await fetch(
        `${API_BASE}/live-support/messages.php?ticket_id=${tickId}`
      );
      const data = await response.json();

      console.log('[ChatBot] Messages API response:', data);

      if (data.success && data.messages && Array.isArray(data.messages)) {
        console.log('[ChatBot] Retrieved ' + data.messages.length + ' messages from API');
        setIsBlockedByAdmin(Boolean(data.ticket_blocked));
        
        // Convert backend messages to chat format
        const newMessages = data.messages.map((msg) => ({
          id: msg.id || `msg-${msg.message_id}`,
          text: msg.message,
          sender: msg.sender === 'admin' ? 'admin' : (msg.sender === 'client' ? 'user' : msg.sender),
          senderName: msg.senderName || 'Unknown',
          timestamp: new Date(msg.timestamp),
          isLiveSupportMessage: true,
        }));

        console.log('[ChatBot] Converted messages:', newMessages);

        // Update messages - append new live support messages
        setMessages((prev) => {
          // Filter out welcome message if we now have escalated messages
          const hasLiveMessages = prev.some((m) => m.isLiveSupportMessage);
          const baseMsgs = prev.filter(
            (m) => !m.isHistoryLoaded || m.isLiveSupportMessage
          );

          if (
            !hasLiveMessages &&
            baseMsgs.length === 1 &&
            baseMsgs[0].id === WELCOME_MESSAGE.id
          ) {
            console.log('[ChatBot] Replacing welcome message with live messages');
            return newMessages;
          }

          // Avoid duplicates
          const existingIds = new Set(baseMsgs.map((m) => m.id));
          const uniqueNew = newMessages.filter((m) => !existingIds.has(m.id));

          console.log('[ChatBot] Appending ' + uniqueNew.length + ' new messages');
          return [...baseMsgs, ...uniqueNew];
        });
      } else {
        console.warn('[ChatBot] API returned unsuccessful response:', data);
      }
    } catch (err) {
      console.error('[ChatBot] Error fetching live support messages:', err);
    }
  };

  // Start polling for live support messages
  const startPolling = (tickId) => {
    console.log('[ChatBot] Starting polling for ticket:', tickId);
    
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      console.log('[ChatBot] Cleared existing poll interval');
    }

    // Fetch immediately
    console.log('[ChatBot] Fetching messages immediately');
    fetchLiveSupportMessages(tickId);

    // Then poll every 2 seconds
    console.log('[ChatBot] Setting up poll interval (2 seconds)');
    pollIntervalRef.current = setInterval(() => {
      console.log('[ChatBot] Poll interval triggered');
      fetchLiveSupportMessages(tickId);
    }, 2000);
  };

  const appendBlockedNotice = () => {
    const blockedNoticeText =
      'You can no longer send messages in this conversation because support has blocked this ticket.';

    setMessages((prev) => {
      const alreadyVisible = prev.some((msg) => msg.text === blockedNoticeText);
      if (alreadyVisible) return prev;

      return [
        ...prev,
        {
          id: `msg-blocked-${Date.now()}`,
          text: blockedNoticeText,
          sender: 'admin',
          timestamp: new Date(),
          isLiveSupportMessage: true,
        },
      ];
    });
  };

  // Handle sending message
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!input.trim() || isThinking) return;

    const messageText = input;
    setInput('');
    setIsThinking(true);

    try {
      // If escalated to live support, send message to backend API
      if (escalated && ticketId) {
        if (isBlockedByAdmin) {
          appendBlockedNotice();
          setIsThinking(false);
          return;
        }

        const user = getCurrentUser();
        const clientName = user?.full_name || user?.username || 'Guest';
        const clientId = user?.user_id || null;

        console.log('[ChatBot] LIVE_SUPPORT_MODE: escapated=' + escalated + ', ticketId=' + ticketId);
        console.log('[ChatBot] Preparing to send message to live support endpoint');

        try {
          const payload = {
            ticket_id: ticketId,
            message: messageText,
            sender_type: 'client',
            sender_id: clientId,
            sender_name: clientName,
          };
          
          console.log('[ChatBot] SEND_MESSAGE_PAYLOAD:', JSON.stringify(payload, null, 2));

          const response = await fetch(
            `${API_BASE}/live-support/send-message.php`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            }
          );

          console.log('[ChatBot] API response status:', response.status);
          console.log('[ChatBot] API response statusText:', response.statusText);
          
          const data = await response.json();
          console.log('[ChatBot] SEND_MESSAGE_RESPONSE:', JSON.stringify(data, null, 2));
          
          if (data.success) {
            console.log('[ChatBot] ✓ Message sent to live support successfully');
            console.log('[ChatBot] Message ID from API:', data.message_id);
            
            // Save to authenticated user's history if logged in
            if (currentUserId) {
              await saveMessageToBackend(currentUserId, messageText, 'user');
            }
            
            // Fetch updated messages
            console.log('[ChatBot] Fetching updated messages from database...');
            await new Promise((resolve) => setTimeout(resolve, 500));
            await fetchLiveSupportMessages(ticketId);
          } else {
            console.error('[ChatBot] API returned error:', data.message);
            if (data.blocked) {
              setIsBlockedByAdmin(true);
              appendBlockedNotice();
            }
          }
        } catch (apiError) {
          console.error('[ChatBot] ✗ Error sending live support message:', apiError);
          console.error('[ChatBot] Error details:', {
            message: apiError.message,
            stack: apiError.stack,
            type: apiError.constructor.name
          });
        }

        setIsThinking(false);
        return;
      }

      // Add user message to chat
      const userMessage = {
        id: `msg-user-${Date.now()}`,
        text: messageText,
        sender: 'user',
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const baseMsgs = prev.filter((m) => m.id !== WELCOME_MESSAGE.id);
        return [...baseMsgs, userMessage];
      });

      // Save user message to backend if authenticated
      if (currentUserId) {
        await saveMessageToBackend(currentUserId, messageText, 'user');
      }

      // Check for escalation trigger
      const lowerInput = messageText.toLowerCase();
      console.log('[ChatBot] Checking for escalation trigger:', lowerInput);
      
      if (
        lowerInput.includes('talk to a real agent') ||
        lowerInput.includes('real agent') ||
        lowerInput.includes('human agent') ||
        lowerInput.includes('speak to agent')
      ) {
        console.log('[ChatBot] ESCALATION TRIGGERED - Creating ticket');
        
        // Get client info
        const user = getCurrentUser();
        const clientName = user?.full_name || user?.username || 'Guest';
        const clientId = user?.user_id || null;
        const clientEmail = user?.email || '';

        console.log('[ChatBot] Client info:', { clientName, clientId, clientEmail });

        // Store conversation context for seamless transfer
        const conversationContext = {
          user_id: clientId,
          client_name: clientName,
          client_email: clientEmail,
          conversation_messages: messages.map((m) => ({
            sender: m.sender,
            senderName: m.senderName || clientName,
            message: m.text,
            timestamp: m.timestamp,
          })),
          escalation_reason: 'User requested to speak with a human agent',
          escalated_at: new Date().toISOString(),
        };

        let escalationSucceeded = false;
        let escalationErrorMessage = '';

        // Create support ticket via backend API
        try {
          // Build conversation history array for transfer
          const conversationHistory = messages
            .filter((msg) => msg.text && msg.sender !== undefined)
            .map((msg) => ({
              sender: msg.sender,
              message: msg.text,
              timestamp: msg.timestamp instanceof Date 
                ? msg.timestamp.toISOString() 
                : new Date(msg.timestamp).toISOString(),
            }));

          console.log('[ChatBot] Sending escalation to API with history:', {
            client_name: clientName,
            client_email: clientEmail,
            client_id: clientId,
            message: userMessage.text,
            priority: 'High',
            conversation_history_count: conversationHistory.length,
          });

          const ticketResponse = await fetch(
            `${API_BASE}/live-support/create.php`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                client_name: clientName,
                client_email: clientEmail,
                client_id: clientId,
                message: userMessage.text,
                priority: 'High',
                conversation_history: conversationHistory,
              }),
            }
          );

          const ticketData = await ticketResponse.json();
          
          console.log('[ChatBot] Escalation API response:', ticketData);
          
          if (ticketData.success) {
            const newTicketId = ticketData.ticket_id;
            console.log('[ChatBot] Support ticket created:', newTicketId);
            escalationSucceeded = true;

            // Store conversation context and escalation data
            conversationContext.ticket_id = newTicketId;
            storeConversationContext(conversationContext);

            // Initialize escalation system with real-time subscriptions
            const escalationSystem = initializeEscalationSystem(newTicketId, (msg) => {
              console.log('[ChatBot] Real-time message received:', msg);
              // Message will be automatically fetched and displayed
              fetchLiveSupportMessages(newTicketId);
            });
            escalationSystemRef.current = escalationSystem;

            // Get message queue for this ticket
            const messageQueue = getQueueForTicket(newTicketId);

            // Add all existing messages to queue
            messages.forEach((msg) => {
              if (msg.text) {
                messageQueue.addMessage({
                  ticketId: newTicketId,
                  message: msg.text,
                  sender_type: msg.sender === 'user' ? 'client' : 'admin',
                  sender_name: clientName,
                  created_at: msg.timestamp.toISOString(),
                  _localId: msg.id,
                });
              }
            });

            // Broadcast escalation to admin dashboard IN REAL-TIME (instant!)
            const escalationData = {
              ticket_id: newTicketId,
              client_name: clientName,
              client_email: clientEmail,
              client_id: clientId,
              subject: `${clientName} - Live Support Request`,
              status: 'active',
              live_support_type: 'escalated',
              created_at: new Date().toISOString(),
              priority: 'High',
              conversation_context: conversationContext,
              escalated_from: 'chatbot',
              message_count: messages.length,
            };

            console.log('[ChatBot] Broadcasting escalation to admin:', escalationData);

            // Broadcast via all channels for INSTANT notification
            broadcastEscalation(escalationData);

            // Also broadcast directly via mock WebSocket for instant updates
            if (mockWebSocket.isConnected()) {
              await mockWebSocket.send(escalationData);
            }

            // Mark this escalation as unread for admin
            addUnreadEscalation({
              ticket_id: newTicketId,
              client_name: clientName,
              client_email: clientEmail,
              priority: 'High',
              received_at: new Date().toISOString(),
            });

            // Subscribe to priority updates for this ticket
            subscribeToPriorityUpdates(newTicketId);

            // Store escalation state
            if (currentUserId) {
              // For authenticated users, store in localStorage
              localStorage.setItem('liveSupportTicketId', newTicketId);
              localStorage.setItem('liveSupportEscalated', 'true');
            } else {
              // For guests, store in sessionStorage (doesn't persist across page reload)
              sessionStorage.setItem('guestChatbotTicketId', newTicketId);
              sessionStorage.setItem('guestChatbotEscalated', 'true');
            }

            setTicketId(newTicketId);
            setEscalated(true);
            setIsBlockedByAdmin(false);

            // Transition from polling to real-time using NEW system
            // Keep polling as fallback, but prioritize real-time events
            startPolling(newTicketId);
          } else {
            console.error('[ChatBot] Escalation failed:', ticketData.message);
            escalationErrorMessage =
              ticketData.message ||
              'Could not connect you to live support right now. Please try again in a moment.';
          }
        } catch (apiError) {
          console.error('[ChatBot] Error creating support ticket:', apiError);
          console.error('[ChatBot] Full error:', apiError);
          escalationErrorMessage =
            'Could not connect you to live support right now. Please try again in a moment.';
        }

        // Simulate typing delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const botResponse = {
          id: `msg-bot-${Date.now()}`,
          text: '✓ Your request has been forwarded to live support. Please wait for an agent. A support representative will be with you shortly.',
          sender: 'bot',
          timestamp: new Date(),
        };


        if (escalationSucceeded) {
          botResponse.text =
            'Your request has been forwarded to live support. Please wait for an agent. A support representative will be with you shortly.';
        } else {
          botResponse.text = escalationErrorMessage;
        }

        setMessages((prev) => [...prev, botResponse]);
        setIsThinking(false);
        return;
      }

      // Find appropriate response (normal bot flow)
      const response = await findResponse(messageText);

      // Simulate typing delay
      const delay = Math.random() * 1000 + 500;
      await new Promise((resolve) => setTimeout(resolve, delay));

      const botResponse = {
        id: `msg-bot-${Date.now()}`,
        text: response,
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botResponse]);

      // Save bot response to backend if authenticated
      if (currentUserId) {
        await saveMessageToBackend(currentUserId, response, 'bot');
      }

      setIsThinking(false);
    } catch (error) {
      console.error('[ChatBot] Error getting response:', error);
      const errorResponse = {
        id: `msg-error-${Date.now()}`,
        text: 'Sorry, I encountered an error. Please try again or contact support.',
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorResponse]);
      setIsThinking(false);
    }
  };

  // Handle quick action button clicks
  const handleQuickAction = (action) => {
    const actionMessages = {
      'Explore Services': 'Can you give me all your services?',
      'Book Appointment': 'I want to book an appointment',
      'Recommendations': 'Can you recommend a service for me?',
      'Prices & Packages': 'What are your pricing and packages?',
      'Working Hours': 'What are your working hours?',
      'Talk to Agent': 'I want to talk to a real agent',
    };

    const message = actionMessages[action];
    if (message) {
      setInput(message);
      // Auto-submit the form
      setTimeout(() => {
        const form = document.querySelector('[data-chat-form]');
        if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
      }, 50);
    }
  };

  const handleRealAgentButtonClick = () => {
    if (isThinking || isLoadingHistory || escalated) return;
    handleQuickAction('Talk to Agent');
  };

  // Format time
  const formatTime = (date) => {
    if (!(date instanceof Date)) {
      date = new Date(date);
    }
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Check if last message is from bot
  const lastMessageIsBot =
    messages.length > 0 && messages[messages.length - 1].sender === 'bot';

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 sm:right-4 w-96 h-[600px] bg-[#fdfaf7] rounded-[24px] shadow-[0_24px_60px_rgba(12,10,8,0.15)] flex flex-col z-50 border border-[#eadfd8]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B7355] to-[#A0826D] text-white p-5 rounded-t-[24px] flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full transition-colors ${
            connectionStatus === 'connected' ? 'bg-emerald-400' :
            connectionStatus === 'disconnected' ? 'bg-red-400' :
            'bg-yellow-400 animate-pulse'
          }`}></div>
          <h3 className="font-semibold text-lg" style={{ fontFamily: 'Sora, sans-serif' }}>
            Hygea Support
            {realtimeActive && escalated && <span className="text-xs font-normal text-emerald-200 ml-2">• Real-time</span>}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:bg-[#7A6249] p-1.5 rounded-lg transition-colors"
          title="Close chat"
        >
          ✕
        </button>
      </div>

      {/* Status indicator if escalated */}
      {escalated && (
        <div className="bg-emerald-50 border-b-2 border-emerald-300 text-emerald-800 px-4 py-2 text-sm font-medium">
          ✓ Connected to Live Support
        </div>
      )}
      {escalated && isBlockedByAdmin && (
        <div className="bg-red-50 border-b border-red-300 text-red-700 px-4 py-2 text-sm font-medium">
          Messaging disabled: this conversation has been blocked by support.
        </div>
      )}

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 bg-[#fdfaf7]">
        {messages.map((msg) => {
          const isUser = msg.sender === 'user' || msg.sender === 'client';
          const isAdmin =
            msg.sender === 'admin' ||
            msg.sender === 'agent' ||
            msg.sender === 'support';
          const alignClass = isUser ? 'justify-end' : 'justify-start';

          return (
            <div key={msg.id} className={`flex ${alignClass}`}>
              <div
                className={`max-w-[88%] sm:max-w-[80%] px-4 py-3 rounded-[16px] shadow-sm ${
                  isUser
                    ? 'bg-[#B89F80] text-white border border-[#ae9472] rounded-br-[6px]'
                    : isAdmin
                    ? 'bg-[#d9efe4] text-[#0b6b57] border border-[#79d7b4] rounded-bl-[6px]'
                    : 'bg-[#f0ebe5] text-[#4a4a4a] border border-[#e7ddd2] rounded-bl-[6px]'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {isAdmin && (
                  <p className="text-xs font-semibold text-[#0b6b57] mb-1">
                    Support Agent
                  </p>
                )}
                <p className="text-sm whitespace-pre-line break-words leading-relaxed">{msg.text}</p>
                <p
                  className={`text-xs mt-1 ${
                    isUser
                      ? 'text-white/85'
                      : isAdmin
                      ? 'text-[#0b6b57]'
                      : 'text-[#999]'
                  }`}
                >
                  {formatTime(msg.timestamp)}
                </p>
              </div>
            </div>
          );
        })}

        {/* Typing indicator */}
        {isThinking && (
          <div className="flex justify-start">
            <div className="bg-[#f0ebe5] text-[#4a4a4a] px-4 py-3 rounded-[16px] rounded-bl-none">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-[#B89F80] rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-[#B89F80] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-[#B89F80] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {!escalated && (
        <div className="px-4 pb-3 bg-[#fdfaf7]">
          <button
            type="button"
            onClick={handleRealAgentButtonClick}
            disabled={isThinking || isLoadingHistory}
            className="w-full flex items-center justify-center gap-2 border border-[#b39a78] text-[#8f7553] bg-[#f6f0e8] hover:bg-[#efe6dc] disabled:bg-[#f1ece5] disabled:text-[#c1b6a8] rounded-full py-2.5 transition-colors font-semibold"
            style={{ fontFamily: 'Sora, sans-serif' }}
            aria-label="Connect to a real support agent"
            title="Connect to a real support agent"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" />
              <path d="M5 19.5C5 16.4624 7.46243 14 10.5 14H13.5C16.5376 14 19 16.4624 19 19.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>I want real agent</span>
          </button>
        </div>
      )}

      {/* Input Area */}
      <form
        onSubmit={handleSendMessage}
        data-chat-form
        className="border-t border-[#eadfd8] p-4 bg-[#fdfaf7]"
      >
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              escalated
                ? (isBlockedByAdmin ? 'Messaging disabled by support' : 'Type your message to the agent...')
                : 'Type your message...'
            }
            disabled={isThinking || (escalated && isBlockedByAdmin)}
            className="flex-1 px-4 py-2.5 border border-[#ddcfc4] bg-white rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#B89F80] disabled:bg-[#f5f0e8] disabled:text-[#bbb] text-[#4a4a4a]"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          />
          <button
            type="submit"
            disabled={isThinking || !input.trim() || (escalated && isBlockedByAdmin)}
            className="bg-[#B89F80] hover:bg-[#A68A6A] disabled:bg-[#ddd] text-white px-4 py-2.5 rounded-[12px] transition-colors font-medium"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatBot;

